import React, { useState, useEffect } from 'react';
import { FileText, Receipt, Ban } from 'lucide-react';

/**
 * Modal ÚNICO pós-pagamento com 3 opções:
 *   Imprimir Cupom Fiscal · Imprimir Cupom Não Fiscal · Não Imprimir
 * Teclado: ← → (ou ↑ ↓) escolhem, ENTER confirma, ESC volta ao pagamento.
 * onConfirm(cupomType, shouldPrint)
 */
const OPTIONS = [
  { key: 'fiscal', type: 'fiscal', print: true, label: 'Imprimir Cupom Fiscal', hint: 'Cupom fiscal (NFC-e) direto na impressora', Icon: Receipt,
    active: 'border-emerald-600 bg-emerald-50 ring-emerald-200', idle: 'hover:border-emerald-400 hover:bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-700' },
  { key: 'nao_fiscal', type: 'nao_fiscal', print: true, label: 'Imprimir Cupom Não Fiscal', hint: 'Cupom simples direto na impressora', Icon: FileText,
    active: 'border-blue-600 bg-blue-50 ring-blue-200', idle: 'hover:border-blue-400 hover:bg-blue-50', iconBg: 'bg-blue-100', iconColor: 'text-blue-700' },
  { key: 'none', type: 'nao_fiscal', print: false, label: 'Não Imprimir', hint: 'Finaliza a venda sem imprimir nada', Icon: Ban,
    active: 'border-slate-700 bg-slate-100 ring-slate-300', idle: 'hover:border-slate-500 hover:bg-slate-50', iconBg: 'bg-slate-200', iconColor: 'text-slate-700' },
];

export const PDVCupomModal = ({ show, onConfirm, onCancel, total, defaultOption = 'none' }) => {
  const defaultIdx = Math.max(0, OPTIONS.findIndex(o => o.key === defaultOption));
  const [selected, setSelected] = useState(defaultIdx);

  useEffect(() => { if (show) setSelected(defaultIdx); }, [show, defaultIdx]);

  useEffect(() => {
    if (!show) return;
    const handler = (e) => {
      const k = e.key;
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(k)) return;
      e.preventDefault();
      e.stopPropagation();
      if (k === 'ArrowLeft' || k === 'ArrowUp') setSelected((s) => (s + OPTIONS.length - 1) % OPTIONS.length);
      else if (k === 'ArrowRight' || k === 'ArrowDown') setSelected((s) => (s + 1) % OPTIONS.length);
      else if (k === 'Enter') { const o = OPTIONS[selected]; onConfirm(o.type, o.print); }
      else if (k === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [show, selected, onConfirm, onCancel]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4" data-testid="cupom-type-modal">
      <div className="bg-white rounded-xl p-6 max-w-3xl w-full shadow-2xl">
        <div className="text-center mb-6">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-600">✓ Pagamento confirmado</p>
          <h3 className="text-3xl font-black text-slate-900 mt-2">Imprimir cupom?</h3>
          {total !== undefined && (
            <p className="text-sm text-slate-600 mt-2">
              Total da venda: <span className="font-black text-blue-600">R$ {Number(total).toFixed(2)}</span>
            </p>
          )}
          <p className="text-xs text-slate-600 mt-3 bg-yellow-50 border border-yellow-300 rounded px-3 py-2 inline-block">
            <kbd className="px-1.5 py-0.5 bg-white border rounded">←</kbd> <kbd className="px-1.5 py-0.5 bg-white border rounded">→</kbd> escolhe · <kbd className="px-1.5 py-0.5 bg-white border rounded">Enter</kbd> confirma <b>{OPTIONS[defaultIdx].label}</b> · a venda finaliza sozinha e o PDV já fica pronto para a próxima
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {OPTIONS.map((o, idx) => {
            const active = selected === idx;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => onConfirm(o.type, o.print)}
                onMouseEnter={() => setSelected(idx)}
                aria-selected={active}
                data-selected={active ? 'true' : 'false'}
                className={`flex flex-col items-center justify-center gap-3 p-6 rounded-lg border-4 transition-all active:scale-95 ${
                  active ? `${o.active} shadow-lg ring-4 scale-[1.02]` : `border-slate-300 bg-white ${o.idle}`
                }`}
                data-testid={`cupom-option-${o.key}`}
              >
                <div className={`p-4 rounded-full ${active ? o.iconBg : 'bg-slate-100'}`}>
                  <o.Icon size={36} className={active ? o.iconColor : 'text-slate-700'} />
                </div>
                <div className="text-center">
                  <p className="text-lg font-black text-slate-900 leading-tight">{o.label}</p>
                  <p className="text-xs text-slate-500 mt-1">{o.hint}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 flex justify-center">
          <button type="button" onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="cupom-cancel">
            ← Voltar ao pagamento <kbd className="ml-1 px-1.5 py-0.5 bg-slate-100 border rounded text-xs">Esc</kbd>
          </button>
        </div>
      </div>
    </div>
  );
};
